import type {
  TaskListResponse,
  TaskStatus,
} from '../../services/api/types.gen';

export type LockedTaskType =
  | 'order'
  | 'visit'
  | 'patient_communication'
  | 'misc_task';
export type ScheduleLockedTask = Pick<
  TaskListResponse,
  'task_type' | 'status'
> & {
  task_type: LockedTaskType;
};

export const LOCKED_ORDER_STATUSES = new Set<TaskStatus>([
  'completed',
  'sent_to_physician',
  'submitted_with_signature',
  'returned_with_signature',
  'sent_no_signature_required',
]);

export const LOCKED_VISIT_STATUSES = new Set<TaskStatus>([
  'submitted_with_signature',
  'pending_clinician_signature',
  'completed',
  'exported',
]);

export const LOCKED_PATIENT_COMMUNICATION_STATUSES = new Set<TaskStatus>([
  'submitted_with_signature',
  'pending_clinician_signature',
  'completed',
]);

export const LOCKED_MISC_TASK_STATUSES = new Set<TaskStatus>([
  'submitted_with_signature',
  'pending_clinician_signature',
  'completed',
]);

const ORDER_TOOLTIP_BY_STATUS: Partial<Record<TaskStatus, string>> = {
  completed:
    'Locked because this order is completed and ready to send to the physician. Use Unlock & Edit from the order detail panel to make changes.',
  sent_to_physician:
    'Locked because this order was sent for physician signature. Use Unlock & Edit from the order detail panel to make changes.',
  submitted_with_signature:
    'Locked because this order already has a signature. Use Unlock & Edit from the order detail panel to make changes.',
  returned_with_signature:
    'Locked because this order was returned with a signature. Use Unlock & Edit from the order detail panel to make changes.',
  sent_no_signature_required:
    'Locked because this order has been sent and does not require a physician signature.',
};

const VISIT_TOOLTIP_BY_STATUS: Partial<Record<TaskStatus, string>> = {
  submitted_with_signature:
    'Locked because this visit already has a signature. Use Edit from the visit detail panel to make changes.',
  pending_clinician_signature:
    'Locked because this visit has been signed by the case manager and needs clinician re-signature.',
  completed: 'Locked because this visit has been approved and completed.',
  exported: 'Locked because this visit has been completed and exported.',
};

const PATIENT_COMMUNICATION_TOOLTIP_BY_STATUS: Partial<
  Record<TaskStatus, string>
> = {
  submitted_with_signature:
    'Locked because this communication already has a signature. Use Unlock & Edit from the communication detail panel to make changes.',
  pending_clinician_signature:
    'Locked because this communication has been signed by the case manager and needs clinician re-signature.',
  completed: 'Locked because this patient communication is completed.',
};

const MISC_TASK_TOOLTIP_BY_STATUS: Partial<Record<TaskStatus, string>> = {
  submitted_with_signature:
    'Locked because this task already has a signature. Use Unlock & Edit from the task detail panel to make changes.',
  pending_clinician_signature:
    'Locked because this task has been signed by the case manager and needs clinician re-signature.',
  completed: 'Locked because this task is completed.',
};

const FALLBACK_LOCKED_TASK_TOOLTIP =
  'Locked task. Open the task detail panel to review available edit actions.';

// Keyed lookup rather than a ternary chain: an unmapped task type must fall
// through to FALLBACK_LOCKED_TASK_TOOLTIP, not silently inherit another type's
// copy (which would point the user at a detail panel that does not apply).
const TOOLTIP_BY_TASK_TYPE: Record<
  LockedTaskType,
  Partial<Record<TaskStatus, string>>
> = {
  order: ORDER_TOOLTIP_BY_STATUS,
  visit: VISIT_TOOLTIP_BY_STATUS,
  patient_communication: PATIENT_COMMUNICATION_TOOLTIP_BY_STATUS,
  misc_task: MISC_TASK_TOOLTIP_BY_STATUS,
};

export function isLockedOrderStatus(
  status: TaskStatus | null | undefined
): boolean {
  return status ? LOCKED_ORDER_STATUSES.has(status) : false;
}

export function isLockedVisitStatus(
  status: TaskStatus | null | undefined
): boolean {
  return status ? LOCKED_VISIT_STATUSES.has(status) : false;
}

export function isLockedPatientCommunicationStatus(
  status: TaskStatus | null | undefined
): boolean {
  return status ? LOCKED_PATIENT_COMMUNICATION_STATUSES.has(status) : false;
}

export function isLockedMiscTaskStatus(
  status: TaskStatus | null | undefined
): boolean {
  return status ? LOCKED_MISC_TASK_STATUSES.has(status) : false;
}

export function isScheduleLockedTask(
  task: Pick<TaskListResponse, 'task_type' | 'status'>
): task is ScheduleLockedTask {
  if (task.task_type === 'order') return isLockedOrderStatus(task.status);
  if (task.task_type === 'visit') return isLockedVisitStatus(task.status);
  if (task.task_type === 'patient_communication') {
    return isLockedPatientCommunicationStatus(task.status);
  }
  if (task.task_type === 'misc_task') {
    return isLockedMiscTaskStatus(task.status);
  }
  return false;
}

/**
 * True when the server refuses document changes for this task.
 *
 * Only misc tasks have that freeze: their attached document IS the record and is
 * hashed into the signed snapshot. Signed visits, orders and communications are
 * schedule-locked but their document endpoints still accept changes, so this is
 * deliberately narrower than {@link isScheduleLockedTask} — widening it would
 * withhold an upload the API allows.
 */
export function isDocumentMutationLocked(
  task: Pick<TaskListResponse, 'task_type' | 'status'>
): boolean {
  return task.task_type === 'misc_task' && isLockedMiscTaskStatus(task.status);
}

export function getScheduleLockedTask(
  task: Pick<TaskListResponse, 'task_type' | 'status'>
): ScheduleLockedTask | null {
  return isScheduleLockedTask(task) ? task : null;
}

export function getLockedTaskTooltip(
  taskType: LockedTaskType,
  status: TaskStatus | null | undefined
): string {
  if (!status) return FALLBACK_LOCKED_TASK_TOOLTIP;
  const copyByStatus = TOOLTIP_BY_TASK_TYPE[taskType];
  return copyByStatus?.[status] ?? FALLBACK_LOCKED_TASK_TOOLTIP;
}
